import { featureFlags } from '@seen/core/featureFlags'
import { useRef } from 'react'
import { useHotkeys as useModifierHotkeys } from 'react-hotkeys-hook'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useFacesheetCardCollapseStore } from '#src/hooks/useFacesheetCardCollapseStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useHotkeysStore } from '#src/hooks/useHotkeys.ts'

const OPEN_OVERLAY_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
].join(', ')

// Shift+arrow is also how a range grows. An existing selection, and any open
// dialog or menu, keep the keys.
function shouldIgnoreFacesheetCardHotkey(event: KeyboardEvent): boolean {
  const target = event.target
  if (target instanceof Element && target.closest(OPEN_OVERLAY_SELECTOR)) {
    return true
  }
  const selection = document.getSelection()
  return Boolean(
    selection && selection.rangeCount > 0 && !selection.isCollapsed
  )
}

// Bound only while a facesheet is mounted. The shared useHotkeys wrapper
// drops ArrowLeft and ArrowRight whenever Shift is held, because those keys
// page the calendar on their own, so this registers with react-hotkeys-hook
// directly. Form fields and contenteditable (the notepad, note editor, and
// questionnaire rich text) are skipped by that hook's defaults.
// `onToggleAll` runs after each shortcut, with the state every card now has.
export function useFacesheetCardHotkeys(
  onToggleAll?: (state: 'expanded' | 'collapsed') => void
): void {
  const enabled = useFeatureFlag(featureFlags.facesheetCardCollapseHotkeys)
  const hotkeysDisabled = useHotkeysStore((state) => state.disabled)
  const active = enabled && !hotkeysDisabled
  // react-hotkeys-hook keeps the first callback it was given.
  const onToggleAllRef = useRef(onToggleAll)
  onToggleAllRef.current = onToggleAll

  useModifierHotkeys(
    Hotkeys.FacesheetCollapseAll,
    () => {
      useFacesheetCardCollapseStore.getState().actions.collapseAll()
      onToggleAllRef.current?.('collapsed')
    },
    {
      enabled: active,
      preventDefault: true,
      ignoreEventWhen: shouldIgnoreFacesheetCardHotkey,
    }
  )
  useModifierHotkeys(
    Hotkeys.FacesheetExpandAll,
    () => {
      useFacesheetCardCollapseStore.getState().actions.expandAll()
      onToggleAllRef.current?.('expanded')
    },
    {
      enabled: active,
      preventDefault: true,
      ignoreEventWhen: shouldIgnoreFacesheetCardHotkey,
    }
  )
}
